'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Grid3x3, List, Check, Calendar } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { filesApi } from '@/lib/api/endpoints';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { LazyImage } from '@/components/LazyImage';
import { format } from 'date-fns';
export interface FileBrowserProps {
  // Selection mode
  selectionMode?: 'none' | 'single' | 'multiple';
  selectedFileIds?: number[];
  onSelectionChange?: (fileIds: number[]) => void;

  // Filters
  initialSearch?: string;
  initialType?: string[];
  initialStatus?: string;

  // Display
  viewMode?: 'grid' | 'list';
  limit?: number;

  // Callbacks
  onFileClick?: (file: any) => void;
  onFileDoubleClick?: (file: any) => void;
}
export function FileBrowser(t0) {
  const $ = _c(91);
  const {
    selectionMode: t1,
    selectedFileIds: t2,
    onSelectionChange,
    initialSearch: t3,
    initialType: t4,
    initialStatus: t5,
    viewMode: t6,
    limit: t7,
    onFileClick,
    onFileDoubleClick
  } = t0;
  const selectionMode = t1 === undefined ? "none" : t1;
  let t8;
  if ($[0] !== t2) {
    t8 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const selectedFileIds = t8;
  const initialSearch = t3 === undefined ? "" : t3;
  let t9;
  if ($[2] !== t4) {
    t9 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const initialType = t9;
  const initialStatus = t5 === undefined ? "active" : t5;
  const initialViewMode = t6 === undefined ? "grid" : t6;
  const limit = t7 === undefined ? 50 : t7;
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [selectedIds, setSelectedIds] = useState(selectedFileIds);
  let t10;
  let t11;
  if ($[4] !== selectedFileIds) {
    t10 = () => {
      setSelectedIds(selectedFileIds);
    };
    t11 = [selectedFileIds];
    $[4] = selectedFileIds;
    $[5] = t10;
    $[6] = t11;
  } else {
    t10 = $[5];
    t11 = $[6];
  }
  useEffect(t10, t11);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  let t12;
  if ($[7] !== debouncedSearch) {
    t12 = debouncedSearch.trim() || undefined;
    $[7] = debouncedSearch;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  const t13 = typeFilter.length > 0 ? typeFilter : undefined;
  const t14 = statusFilter || undefined;
  let t15;
  if ($[9] !== limit || $[10] !== t12 || $[11] !== t13 || $[12] !== t14) {
    t15 = {
      search: t12,
      type: t13,
      status: t14,
      limit
    };
    $[9] = limit;
    $[10] = t12;
    $[11] = t13;
    $[12] = t14;
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const query = t15;
  let t16;
  if ($[14] !== query) {
    t16 = {
      queryKey: ["files", query],
      queryFn: async t17 => {
        const {
          pageParam
        } = t17;
        return filesApi.getFiles({
          ...query,
          nextCursor: pageParam || undefined
        });
      },
      getNextPageParam: _temp,
      initialPageParam: "",
      staleTime: 5000
    };
    $[14] = query;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t16);
  let t17;
  bb0: {
    if (!data?.pages) {
      let t18;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = [];
        $[16] = t18;
      } else {
        t18 = $[16];
      }
      t17 = t18;
      break bb0;
    }
    let t18;
    if ($[17] !== data.pages) {
      t18 = data.pages.flatMap(_temp2);
      $[17] = data.pages;
      $[18] = t18;
    } else {
      t18 = $[18];
    }
    t17 = t18;
  }
  const files = t17;
  let t18;
  if ($[19] !== onFileClick || $[20] !== onSelectionChange || $[21] !== selectedIds || $[22] !== selectionMode) {
    t18 = (file, event) => {
      if (selectionMode === "none") {
        onFileClick?.(file);
        return;
      }
      event.preventDefault();
      if (selectionMode === "single") {
        const newSelection = selectedIds.includes(file.id) ? [] : [file.id];
        setSelectedIds(newSelection);
        onSelectionChange?.(newSelection);
      } else {
        if (selectionMode === "multiple") {
          const newSelection_0 = selectedIds.includes(file.id) ? selectedIds.filter(id => id !== file.id) : [...selectedIds, file.id];
          setSelectedIds(newSelection_0);
          onSelectionChange?.(newSelection_0);
        }
      }
      onFileClick?.(file);
    };
    $[19] = onFileClick;
    $[20] = onSelectionChange;
    $[21] = selectedIds;
    $[22] = selectionMode;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  const handleFileClick = t18;
  let t19;
  if ($[24] !== onFileDoubleClick) {
    t19 = file_0 => {
      onFileDoubleClick?.(file_0);
    };
    $[24] = onFileDoubleClick;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  const handleFileDoubleClick = t19;
  const getFilePreview = _temp3;
  let t20;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e => setSearchQuery(e.target.value);
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== searchQuery) {
    t21 = <input type="text" placeholder="Search media items..." value={searchQuery} onChange={t20} className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent" />;
    $[27] = searchQuery;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />;
    $[29] = t22;
  } else {
    t22 = $[29];
  }
  let t23;
  if ($[30] !== t21) {
    t23 = <div className="flex-1 relative">{t21}{t22}</div>;
    $[30] = t21;
    $[31] = t23;
  } else {
    t23 = $[31];
  }
  let t24;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setViewMode("grid");
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  const t25 = viewMode === "grid" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50";
  let t26;
  if ($[33] !== t25) {
    t26 = cn("inline-flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors cursor-pointer", t25);
    $[33] = t25;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Grid3x3 className="w-4 h-4" />;
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== t26) {
    t28 = <button onClick={t24} className={t26} title="Grid view">{t27}</button>;
    $[36] = t26;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setViewMode("list");
    $[38] = t29;
  } else {
    t29 = $[38];
  }
  const t30 = viewMode === "list" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50";
  let t31;
  if ($[39] !== t30) {
    t31 = cn("inline-flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors cursor-pointer border-l border-gray-300", t30);
    $[39] = t30;
    $[40] = t31;
  } else {
    t31 = $[40];
  }
  let t32;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <List className="w-4 h-4" />;
    $[41] = t32;
  } else {
    t32 = $[41];
  }
  let t33;
  if ($[42] !== t31) {
    t33 = <button onClick={t29} className={t31} title="List view">{t32}</button>;
    $[42] = t31;
    $[43] = t33;
  } else {
    t33 = $[43];
  }
  let t34;
  if ($[44] !== t28 || $[45] !== t33) {
    t34 = <div className="inline-flex rounded-md border border-gray-300 bg-white">{t28}{t33}</div>;
    $[44] = t28;
    $[45] = t33;
    $[46] = t34;
  } else {
    t34 = $[46];
  }
  let t35;
  if ($[47] !== t23 || $[48] !== t34) {
    t35 = <div className="flex gap-3 items-center">{t23}{t34}</div>;
    $[47] = t23;
    $[48] = t34;
    $[49] = t35;
  } else {
    t35 = $[49];
  }
  let t36;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = e_0 => setStatusFilter(e_0.target.value);
    $[50] = t36;
  } else {
    t36 = $[50];
  }
  let t37;
  let t38;
  let t39;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <option value="">All Files</option>;
    t38 = <option value="active">Active</option>;
    t39 = <option value="archived">Archived</option>;
    $[51] = t37;
    $[52] = t38;
    $[53] = t39;
  } else {
    t37 = $[51];
    t38 = $[52];
    t39 = $[53];
  }
  let t40;
  if ($[54] !== statusFilter) {
    t40 = <select value={statusFilter} onChange={t36} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer">{t37}{t38}{t39}</select>;
    $[54] = statusFilter;
    $[55] = t40;
  } else {
    t40 = $[55];
  }
  const t41 = typeFilter[0] || "";
  let t42;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = e_1 => setTypeFilter(e_1.target.value ? [e_1.target.value] : []);
    $[56] = t42;
  } else {
    t42 = $[56];
  }
  let t43;
  let t44;
  let t45;
  let t46;
  let t47;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <option value="">All Types</option>;
    t44 = <option value="image">Images</option>;
    t45 = <option value="video">Videos</option>;
    t46 = <option value="document">Documents</option>;
    t47 = <option value="other">Other</option>;
    $[57] = t43;
    $[58] = t44;
    $[59] = t45;
    $[60] = t46;
    $[61] = t47;
  } else {
    t43 = $[57];
    t44 = $[58];
    t45 = $[59];
    t46 = $[60];
    t47 = $[61];
  }
  let t48;
  if ($[62] !== t41) {
    t48 = <select value={t41} onChange={t42} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer">{t43}{t44}{t45}{t46}{t47}</select>;
    $[62] = t41;
    $[63] = t48;
  } else {
    t48 = $[63];
  }
  let t49;
  if ($[64] !== selectedIds.length) {
    t49 = selectedIds.length > 0 && <div className="ml-auto text-sm text-gray-600">{selectedIds.length} selected</div>;
    $[64] = selectedIds.length;
    $[65] = t49;
  } else {
    t49 = $[65];
  }
  let t50;
  if ($[66] !== t40 || $[67] !== t48 || $[68] !== t49) {
    t50 = <div className="flex gap-3 items-center">{t40}{t48}{t49}</div>;
    $[66] = t40;
    $[67] = t48;
    $[68] = t49;
    $[69] = t50;
  } else {
    t50 = $[69];
  }
  let t51;
  if ($[70] !== t35 || $[71] !== t50) {
    t51 = <div className="border-b border-gray-200 p-4 space-y-3">{t35}{t50}</div>;
    $[70] = t35;
    $[71] = t50;
    $[72] = t51;
  } else {
    t51 = $[72];
  }
  let t52;
  if ($[73] !== files || $[74] !== handleFileClick || $[75] !== handleFileDoubleClick || $[76] !== isLoading || $[77] !== selectedIds || $[78] !== selectionMode || $[79] !== viewMode) {
    t52 = isLoading && files.length === 0 ? <div className="flex justify-center items-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div> : files.length === 0 ? <div className="text-center py-20 bg-gray-50 rounded-lg border border-dashed border-gray-200"><p className="text-gray-500">No files found.</p></div> : viewMode === "grid" ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 gap-4">{files.map(file_2 => {
        const isSelected = selectedIds.includes(file_2.id);
        return <div key={file_2.id} onClick={e_2 => handleFileClick(file_2, e_2)} onDoubleClick={() => handleFileDoubleClick(file_2)} className={cn("relative group cursor-pointer rounded-lg border-2 transition-all overflow-hidden", isSelected ? "border-primary-500 ring-2 ring-primary-200" : "border-gray-200 hover:border-gray-300")}>{selectionMode !== "none" && <div className="absolute top-2 right-2 z-10"><div className={cn("w-6 h-6 rounded border-2 flex items-center justify-center transition-all", isSelected ? "bg-primary-500 border-primary-500" : "bg-white border-gray-300 group-hover:border-gray-400")}>{isSelected && <Check className="w-4 h-4 text-white" />}</div></div>}<div className="aspect-square bg-gray-50">{getFilePreview(file_2)}</div><div className="p-2 bg-white"><p className="text-xs font-medium text-gray-900 truncate" title={file_2.name}>{file_2.name}</p><p className="text-xs text-gray-500 truncate">{file_2.createdAt && format(new Date(file_2.createdAt), "MMM d, yyyy")}</p></div></div>;
      })}</div> : <div className="space-y-1">{files.map(file_3 => {
        const isSelected_0 = selectedIds.includes(file_3.id);
        return <div key={file_3.id} onClick={e_3 => handleFileClick(file_3, e_3)} onDoubleClick={() => handleFileDoubleClick(file_3)} className={cn("flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors", isSelected_0 ? "bg-primary-50 border border-primary-200" : "hover:bg-gray-50 border border-transparent")}>{selectionMode !== "none" && <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0", isSelected_0 ? "bg-primary-500 border-primary-500" : "bg-white border-gray-300")}>{isSelected_0 && <Check className="w-3 h-3 text-white" />}</div>}<div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 bg-gray-100">{getFilePreview(file_3)}</div><div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900 truncate">{file_3.name}</p><p className="text-xs text-gray-500">{file_3.contentType} •{" "}{file_3.size ? `${(file_3.size / 1024).toFixed(1)} KB` : "Unknown size"}</p></div><div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0"><Calendar className="w-3 h-3" />{file_3.createdAt && format(new Date(file_3.createdAt), "MMM d, yyyy")}</div></div>;
      })}</div>;
    $[73] = files;
    $[74] = handleFileClick;
    $[75] = handleFileDoubleClick;
    $[76] = isLoading;
    $[77] = selectedIds;
    $[78] = selectionMode;
    $[79] = viewMode;
    $[80] = t52;
  } else {
    t52 = $[80];
  }
  let t53;
  if ($[81] !== fetchNextPage || $[82] !== hasNextPage || $[83] !== isFetchingNextPage) {
    t53 = hasNextPage && <div className="flex justify-center pt-6"><button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">{isFetchingNextPage ? "Loading more..." : "Load More"}</button></div>;
    $[81] = fetchNextPage;
    $[82] = hasNextPage;
    $[83] = isFetchingNextPage;
    $[84] = t53;
  } else {
    t53 = $[84];
  }
  let t54;
  if ($[85] !== t52 || $[86] !== t53) {
    t54 = <div className="flex-1 overflow-y-auto p-4">{t52}{t53}</div>;
    $[85] = t52;
    $[86] = t53;
    $[87] = t54;
  } else {
    t54 = $[87];
  }
  let t55;
  if ($[88] !== t51 || $[89] !== t54) {
    t55 = <div className="flex flex-col h-full bg-white">{t51}{t54}</div>;
    $[88] = t51;
    $[89] = t54;
    $[90] = t55;
  } else {
    t55 = $[90];
  }
  return t55;
}
function _temp3(file_1) {
  if (file_1.contentType?.startsWith("image/")) {
    return <LazyImage src={file_1.url} alt={file_1.name} className="w-full h-full object-cover" />;
  }
  return <div className="flex items-center justify-center w-full h-full bg-gray-100"><span className="text-2xl font-semibold text-gray-400">{file_1.name?.charAt(0)?.toUpperCase() || "?"}</span></div>;
}
function _temp2(page) {
  return page.files;
}
function _temp(lastPage) {
  return lastPage.pagination?.nextCursor || undefined;
}